import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { initialLegalEntityPreviousNamesSurnamesFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useLEPreviousNamesSurnamesSchema } from 'pages/LegalEntitiesPage/schemaValidation';
import { useScroll } from 'hooks/useScroll';
import { ActionTypes } from 'state/actions/alert';
import { Box } from '@mui/system';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';

export interface ILegalEntityPreviousNamesSurnamesFormValues {
  legalUniqueId?: string;
  firstName: string;
  middleNames: string;
  lastName: string;
  version?: string;
}

export interface ILegalEntityForm {
  onCancel?: () => void;
  onSubmit: (arg: ILegalEntityPreviousNamesSurnamesFormValues | any) => void;
  initialFormValues?: ILegalEntityPreviousNamesSurnamesFormValues | null;
  isEdit?: boolean;
}

const LegalEntityPreviousNamesSurnamesForm = ({
  onCancel,
  onSubmit,
  initialFormValues,
  isEdit = false,
}: ILegalEntityForm) => {
  const { id } = useParams<{ id: string }>();
  const { state: routerState }: any = useLocation();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const { t } = useTranslations();
  const { schema } = useLEPreviousNamesSurnamesSchema();
  const { elRef, scrollToError } = useScroll();
  const { setAlert, clearAlert, samePageAlert } = useAlert();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    getValues,
  } = useForm<ILegalEntityPreviousNamesSurnamesFormValues>({
    defaultValues: initialFormValues || initialLegalEntityPreviousNamesSurnamesFormValues,
    resolver: yupResolver(
      schema,
    ) as unknown as Resolver<ILegalEntityPreviousNamesSurnamesFormValues>,
  });

  useEffect(() => {
    if (routerState?.form) {
      reset({
        ...getValues(),
        ...routerState.form,
      });
    } else {
      reset(initialFormValues || initialLegalEntityPreviousNamesSurnamesFormValues);
    }
  }, [reset, initialFormValues, routerState]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formChanged = (
    initialValues?: ILegalEntityPreviousNamesSurnamesFormValues | null,
    currentValues?: ILegalEntityPreviousNamesSurnamesFormValues | null,
  ): boolean => {
    const dataChanged =
      initialValues?.firstName !== currentValues?.firstName ||
      initialValues?.middleNames !== currentValues?.middleNames ||
      initialValues?.lastName !== currentValues?.lastName;
    return dataChanged;
  };

  const onSubmitForm = async (data: any) => {
    if (!formChanged(initialFormValues, data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    setIsSubmitLoading(true);
    if (initialFormValues && isEdit) {
      onSubmit({
        ...initialFormValues,
        ...data,
      });
      setIsSubmitLoading(false);
    } else {
      onSubmit({
        legalUniqueId: id as string,
        firstName: data.firstName,
        middleNames: data.middleNames,
        lastName: data.lastName,
      });
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Grid container ref={elRef}>
        <Grid size={{ xs: 12 }} container columnSpacing={2}>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <FormInput
              name="firstName"
              label={`${t('legalEntityDetails.previousNamesSurnamesTab.firstName.column')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <FormInput
              name="middleNames"
              label={t('legalEntityDetails.previousNamesSurnamesTab.middleName.column')}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <FormInput
              name="lastName"
              label={`${t('legalEntityDetails.previousNamesSurnamesTab.surname.column')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save-button"
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
              />
            </Grid>

            {onCancel && (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
            )}
          </Grid>
          <Grid sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      {samePageAlert && (
        <Box sx={{ marginBottom: '10px', mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </form>
  );
};

export { LegalEntityPreviousNamesSurnamesForm };
